import { venueConfig } from '@beat-venues/common-types';
import {
    Button,
    Carousel,
    Container,
    CoverEvent,
    EventDetail,
    EventDetailWrapper,
    EventPracticalTile,
    Heading,
    Logo,
    Modal,
    PointerLightIcon,
    Seat,
    SeatsSolidIcon,
    Text,
    TimingCard,
} from '@beat-venues/ui/react';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// eslint-disable-next-line @nx/enforce-module-boundaries
import externalLink from '/assets/ticket-master/external-link-icon.svg';

import { ICalTile, MobilityTile } from '../../components/event-tiles';
import { VipTile } from '../../components/event-tiles/vip-tile';
import withTransaction, {
    WithTransactionProps,
} from '../../hoc/withTransaction';
import { useAnalytics } from '../../hooks/use-analytics';
import { MappedContentTile } from '@beat-venues/contentful';

type VenueContent = { title: string; contentTiles: MappedContentTile[] }[];

const EventDetails: React.FC<WithTransactionProps> = ({
    transactions,
}): ReactElement => {
    const { t, i18n } = useTranslation('translation');
    const { eventId } = useParams();
    const navigate = useNavigate();

    const mobility = {
        nl: 'mobiliteit',
        en: 'mobility',
        fr: 'mobilite',
    };
    const ids = eventId?.split('-');
    const event =
        ids &&
        transactions?.find(
            (item) => item.orderId === ids[0] && item.eventId === ids[1]
        );

    const [showModal, setShowModal] = useState(false);

    const { triggerPageView } = useAnalytics();

    useEffect(() => {
        triggerPageView();
        if (!event) {
            navigate('/');
        }
        const closeModal = () => {
            setShowModal(false);
        };
        window.addEventListener('popstate', closeModal);
        return () => {
            window.removeEventListener('popstate', closeModal);
        };
    }, []);

    const { data: venueContent } = useQuery<VenueContent>({
        queryKey: ['venue-info', i18n.language],
        queryFn: () =>
            fetch('/api/contentful/venue-info', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    locale: i18n.language,
                    tag: 'channel-my-beat',
                    venue: event?.venueId,
                }),
            }).then((res) => res.json()),
    });

    return (
        <Container className="mb-12 mt-8 px-6 lg:px-0">
            <div className="flex-cols flex grid-cols-3 flex-wrap gap-2 gap-y-4 md:gap-x-4 lg:grid">
                {/*event image*/}
                {event?.image && (
                    <EventDetailWrapper type="image">
                        <CoverEvent
                            attendanceBanner={
                                t('events.attendanceBanner') || ''
                            }
                            image={event.image}
                        />
                    </EventDetailWrapper>
                )}
                {/*event detail*/}
                {event && (
                    <EventDetailWrapper
                        className="w-full md:w-auto"
                        type="content"
                    >
                        <EventDetail
                            date={
                                (event.datePrefix
                                    ? t('date.from', { date: event.date })
                                    : event.date) || ''
                            }
                            name={event.name}
                            tourName={event.tourName}
                            venueId={event.venueId}
                            venueName={t(`venues.${event.venueId}.name`)}
                        />
                    </EventDetailWrapper>
                )}
                {/*timings*/}
                {event?.timings && (
                    <EventDetailWrapper
                        className="w-full p-8 md:w-auto"
                        type="timing"
                    >
                        <div>
                            <TimingCard
                                className="p-0"
                                timings={event.timings[0]?.schedule ?? []}
                                title="Timings"
                            />

                            {event.timings.length === 0 && (
                                <Text className="mb-2">
                                    {t('timings.noTiming')}
                                </Text>
                            )}
                        </div>
                    </EventDetailWrapper>
                )}
                {/*vip*/}
                {event && <VipTile event={event} />}
                {/*TM*/}
                <EventDetailWrapper
                    className="relative w-full md:w-auto"
                    type="content"
                >
                    <div className="relative flex h-full flex-col items-center justify-between gap-5">
                        <Heading as="h4" className="font-normal">
                            {t('events.TMTitle')}
                        </Heading>
                        {!event?.isVip ? (
                            <Button
                                className="-mt-2 mb-1 flex gap-1 bg-[#024DDF] hover:bg-[#0138a7]"
                                href={
                                    process.env[
                                        `VITE_TICKET_MASTER_URI_${i18n.language.toUpperCase()}`
                                    ]
                                }
                                size="lg"
                            >
                                <Logo
                                    className="w-24"
                                    venueId="ticket-master"
                                />
                                <img
                                    alt="external Link"
                                    src={externalLink}
                                    width={20}
                                />
                            </Button>
                        ) : (
                            <Button
                                className="text-buttonTertiary bg-buttonTertiary mb-3 flex items-center justify-center gap-1"
                                href={
                                    process.env[
                                        `VITE_IXPOLE_PACKAGES_URI_${i18n.language.toUpperCase()}`
                                    ]
                                }
                                intent="tertiary"
                                isMainBrand={true}
                            >
                                <Logo
                                    className="mr-2 w-8"
                                    size="sm"
                                    venueId="vip"
                                />
                                <Text size="xl">
                                    {t('events.vipTickets.ctaLabels.tickets')}
                                </Text>
                                <img
                                    alt="external Link"
                                    src={externalLink}
                                    width={20}
                                />
                            </Button>
                        )}
                        <Text>
                            {t('events.TMSubTitle', {
                                tickets: event?.ticketAmount,
                                date: event?.date,
                            })}
                        </Text>
                    </div>
                </EventDetailWrapper>

                {event && (
                    <EventDetailWrapper
                        className="order-4 w-full p-8 md:order-6 md:w-auto"
                        type="timing"
                    >
                        <div>
                            {event.seatInfo && (
                                <Seat
                                    blockTitle={t('events.seat.block')}
                                    className="md:w-full"
                                    gaSeats={event.gaSeatInfo}
                                    isModal={false}
                                    levelTitle={t('events.seat.level')}
                                    noSeatLabel={t('events.seat.noSeat') || ''}
                                    rowTitle={t('events.seat.row')}
                                    seatTitle={t('events.seat.seat')}
                                    seats={event.seatInfo}
                                    title={t('events.seat.title') || ''}
                                />
                            )}
                        </div>
                    </EventDetailWrapper>
                )}
            </div>
            <div className="mt-4 flex grid-cols-3 flex-col flex-wrap gap-2 gap-y-4 md:gap-x-4 lg:grid">
                {event?.floorPlan?.url && (
                    <EventPracticalTile
                        className="py-7"
                        icon={<SeatsSolidIcon size="xl" />}
                        subTitle={t('events.floorPlanSubTitle') || ''}
                        title={t('events.floorPlanTitle')}
                        onClick={() => setShowModal(true)}
                    />
                )}

                <EventPracticalTile
                    className="py-7"
                    href={`${
                        venueConfig(event?.venueId ?? 'afas-dome', 'vite').url
                    }/${i18n.language}/${(mobility as never)[i18n.language]}`}
                    icon={<PointerLightIcon size="xl" />}
                    subTitle={t('events.mobilitySubTitle') || ''}
                    title={t('events.mobilityTitle')}
                />

                {/*ical*/}
                {event && <ICalTile event={event} />}
                {/*mobility*/}
                {event?.mobilityInfo &&
                    event?.mobilityInfo.length > 0 &&
                    event?.floorPlan?.url && (
                        <MobilityTile mobilityInfo={event.mobilityInfo} />
                    )}
            </div>
            {event?.mobilityInfo &&
                event?.mobilityInfo.length > 0 &&
                !event?.floorPlan?.url && (
                    <div className="mt-4 flex grid-cols-3 flex-col flex-wrap gap-2 gap-y-4 md:gap-x-4 lg:grid">
                        <MobilityTile mobilityInfo={event.mobilityInfo} />
                    </div>
                )}
            <Modal
                className="lg:max-auto w-full md:my-[2vh] md:max-h-[96vh] md:max-w-screen-2xl"
                isShown={showModal}
                onClose={() => setShowModal(false)}
            >
                <Modal.Header onClose={() => setShowModal(false)} />
                <Modal.Content className="flex flex-col pb-4 xl:flex-row xl:px-0 xl:pb-0">
                    {event?.floorPlan?.isSvg ? (
                        <object
                            data={event?.floorPlan?.url}
                            height="100%"
                            title="floor plan"
                            width="100%"
                        ></object>
                    ) : (
                        <img
                            alt="floor plan"
                            height="100%"
                            src={event?.floorPlan?.url}
                            width="fit-content"
                        />
                    )}
                    {event?.seatInfo && (
                        <Seat
                            blockTitle={t('events.seat.block')}
                            className="px-3"
                            gaSeats={event.gaSeatInfo}
                            isModal={true}
                            levelTitle={t('events.seat.level')}
                            noSeatLabel={t('events.seat.noSeat') || ''}
                            rowTitle={t('events.seat.row')}
                            seatTitle={t('events.seat.seat')}
                            seats={event.seatInfo}
                            title={t('events.seat.title') || ''}
                        />
                    )}
                </Modal.Content>
            </Modal>
            {venueContent && venueContent.length > 0 && (
                <div className="mt-20 flex grid-cols-3 flex-col flex-wrap gap-2 gap-y-4 md:gap-x-4 lg:grid">
                    {venueContent.map((content, index) => {
                        return (
                            content?.contentTiles && (
                                <div key={`${index}-${content.title}`}>
                                    {content.title && (
                                        <Heading size="h4">
                                            {content.title}
                                        </Heading>
                                    )}
                                    <Carousel content={content?.contentTiles} />
                                </div>
                            )
                        );
                    })}
                </div>
            )}
        </Container>
    );
};

const EventDetailPage = withTransaction(EventDetails);
export { EventDetailPage };
